import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { AddLocationAlt, Payment } from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import GenericBreadCrums from "@components/Breadcrumbs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGeolocation } from "@gis/components/useGeoLocation";
import UnderTakingConsent from "@modules/stepper/components/UnderTakingConsent";
import LegacyAmenityDetails from "./components/LegacyAmenityDetails";
import { StepsButton } from "@modules/stepper/components/StepsButtons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLegacySubStagesThunk,
  submitFinalLegacyAccessApplicationThunk,
  submitLegacyAccessApplicationThunk,
  submitLegacyRowApplicationThunk,
} from "./redux/thunk/legacyThunk";
import { toast } from "react-toastify";
import { AppDispatch, RootReducerType } from "@store/store";
import LegacyAmenityDetailsRow from "./components/LegacyAmenityDetailsRow";
import { UnderTakingConsentRow } from "@modules/stepper-row/components/UnderTakingConsentRow";
import { SiteRoadwaysAccess } from "@modules/stepper/components/SiteRoadWaysAccess";
import UploadDocument from "@modules/stepper/components/UploadDocuments";
import LegacyUploadDocumentAccess from "./components/LegacyUploadDocumentsAccess";
import { SiteRoadwayInformationSection } from "@modules/stepper/components/SiteRoadways";
import { LegacySiteRoadwaysAccess } from "./components/LegacySiteRoadwaysAccess";
import { setLegacyField } from "./redux/slice/legacySlice";
import LegacySiteRoadwaysROW from "./components/LegacySiteRoadwaysROW";
import RowLegacyUploadDocuments from "./components/RowLegacyUploadDocuments";
import LegacyUnderTakingConsentRow from "./components/LegacyUnderTakingConsentRow";
import { setAmenityROWField } from "@modules/stepper-row/redux/amenitesROWSlice";
import {
  fetchApplicationDetails,
  fetchApplicationDetailsRow,
} from "@modules/provisional-payment/redux/thunk/application-detailThunk";
import LegacyUnderTakingConsent from "./components/LegacyUndertakingConsent";
import { resetSiteRoadWaysState } from "@modules/stepper/redux/siteRoadwaysSlice";
import { LegacyStepsButton } from "./components/LegacyStepsButton";
import LegacyPaymentDetail from "./components/LegacyPaymentDetail";

const steps = [
  { label: "Amenities Location Details", icon: <AddLocationAlt /> },
  { label: "Site & Highway", icon: <ReceiptLongIcon /> },
  { label: "Upload Documents", icon: <ReceiptLongIcon /> },
  { label: "Undertakings & Consents", icon: <ReceiptLongIcon /> },
  { label: "Payment Details", icon: <ReceiptLongIcon /> },
];

const LegacyApplicationUpdate: React.FC = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  const { license_type, activeStep } = useSelector(
    (state: RootReducerType) => state.accessLegacy,
  );

  const setLegacyActiveStep = useCallback(
    (step: number | ((prev: number) => number)) => {
      const newStep = typeof step === "function" ? step(activeStep) : step;
      dispatch(setLegacyField({ name: "activeStep", value: newStep }));
    },
    [dispatch, activeStep],
  );
  // STEP STATE (local instead of redux)

  const [stepValidation, setStepValidation] = useState({
    amenity: false,
    roadway: false,
    documents: false,
    consent: false,
    payment: false,
  });

  const [rowStepValid, setRowStepValid] = useState(false);
  const [rowConsentValid, setRowConsentValid] = useState(false);
  const [rowPaymentValid, setRowPaymentValid] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState<any>(null);
  const [applicationDetailsRow, setApplicationDetailsRow] = useState<any>(null);

  // UI STATE
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);
  const [submitHandler, _setSubmitHandler] = useState<
    (() => Promise<boolean | void> | boolean | void) | null
  >(null);
  const [submitHandlerStep, setSubmitHandlerStep] = useState<number | null>(
    null,
  );

  const [rowSubmitHandler, setRowSubmitHandler] = useState<
    (() => Promise<boolean | void> | boolean | void) | null
  >(null);
  const setRowSubmitHandlerVoid =
    setRowSubmitHandler as unknown as React.Dispatch<
      React.SetStateAction<(() => Promise<void>) | null>
    >;
  const [isConsentValid, setIsConsentValid] = useState(false);

  const handleSetROWStepValid = React.useCallback((value: boolean) => {
    setRowStepValid(value);
  }, []);

  const handleSetROWConsentValid = React.useCallback((value: boolean) => {
    setRowConsentValid(value);
  }, []);

  const handleSetROWPaymentValid = React.useCallback((value: boolean) => {
    setRowPaymentValid(value);
  }, []);

  const handleSetAmenityValid = React.useCallback((value: boolean) => {
    setStepValidation((prev) => ({
      ...prev,
      amenity: value,
    }));
  }, []);

  const handleSetRoadwayValid = React.useCallback((value: boolean) => {
    setStepValidation((prev) => ({
      ...prev,
      roadway: value,
    }));
  }, []);

  const handleSetDocumentsValid = React.useCallback((value: boolean) => {
    setStepValidation((prev) => ({
      ...prev,
      documents: value,
    }));
  }, []);

  const handleSetConsentValid = React.useCallback((value: boolean) => {
    setStepValidation((prev) => ({
      ...prev,
      consent: value,
    }));
  }, []);

  const handleSetPaymentValid = React.useCallback((value: boolean) => {
    setStepValidation((prev) => ({
      ...prev,
      payment: value,
    }));
  }, []);

  const setSubmitHandler = React.useCallback(
    (handler: () => Promise<boolean | void> | boolean | void) => {
      _setSubmitHandler(() => handler);
      setSubmitHandlerStep(activeStep);
    },
    [activeStep],
  );

  const fetchROW = async (id: any) => {
    try {
      const res = await dispatch(fetchApplicationDetailsRow(id)).unwrap();
      setApplicationDetailsRow(res);
      return res;
    } catch (error) {
      console.error("Error fetching ROW details:", error);
    }
  };

  const fetchAccess = async (id: any) => {
    try {
      const res = await dispatch(fetchApplicationDetails(id)).unwrap();

      setApplicationDetails(res);
      return res;
    } catch (error) {
      console.error("Error fetching Access details:", error);
    }
  };

  const [searchParams] = useSearchParams();
  const access = searchParams.get("access")
    ? searchParams.get("access")
    : undefined;
  const row = searchParams.get("row") ? searchParams.get("row") : undefined;
  const rowActiveStepStorageKey = row ? `legacy-row-active-step-${row}` : null;
  const rowDetailsPath = "/application-details-row";
  // ? `/application-details-row?row=${row}`
  // : "/application-details-row";
  const moduleType = row ? "row" : "access";

  useEffect(() => {
    // Reset step whenever a different legacy case is opened.
    dispatch(setLegacyField({ name: "activeStep", value: 0 }));
  }, [dispatch]);

  // SAVE ROW ID TO REDUX ON MOUNT
  useEffect(() => {
    if (row || access) {
      dispatch(setLegacyField({ name: "access_id", value: row || access }));
      dispatch(resetSiteRoadWaysState());

      if (row) {
        dispatch(
          setAmenityROWField({
            name: "access_id",
            value: Number(row) || row,
          }),
        );
      }
    }
  }, [row, access, dispatch]);

  useEffect(() => {
    // Fetch once at page level so sub-stages request is visible even before Payment step mounts.
    dispatch(fetchLegacySubStagesThunk());
  }, [dispatch]);

  // STEP SIDE PANEL CONTROL
  useEffect(() => {
    setIsSidePanelCollapsed(activeStep > 0);
  }, [activeStep]);

  // Restore ROW step on refresh based on stored step number (1..5).
  useEffect(() => {
    if (moduleType !== "row" || !rowActiveStepStorageKey) return;

    const storedStep = sessionStorage.getItem(rowActiveStepStorageKey);

    if (storedStep === "1") {
      setLegacyActiveStep(0);
    } else if (storedStep === "2") {
      setLegacyActiveStep(1);
    } else if (storedStep === "3") {
      setLegacyActiveStep(2);
    } else if (storedStep === "4") {
      setLegacyActiveStep(3);
    } else if (storedStep === "5") {
      setLegacyActiveStep(4);
    }
  }, [moduleType, rowActiveStepStorageKey]);

  // Store current ROW step as human-readable step number (1..5).
  useEffect(() => {
    if (moduleType !== "row" || !rowActiveStepStorageKey) return;

    const stepNumber = String(activeStep + 1);
    sessionStorage.setItem(rowActiveStepStorageKey, stepNumber);
  }, [activeStep, moduleType, rowActiveStepStorageKey]);

  // STEP NAVIGATION (you can hook this later to buttons)
  const goToNextStep = async () => {
    if (activeStep === steps.length - 1) {
      dispatch(resetSiteRoadWaysState());
      navigate(`/application-submitted?id=${row || access}&legacy=true`, {
        replace: true,
      });
      return;
    }
    setLegacyActiveStep((prev: number) => prev + 1);
  };

  const goToPreviousStep = () => {
    if (activeStep === 0)
      return navigate("/application-dashboard", { replace: true });
    setLegacyActiveStep((prev: number) => Math.max(prev - 1, 0));
  };

  const handleLegacySubmit = async () => {
    try {
      if (moduleType === "row") {
        const submitRes = await dispatch(
          submitLegacyRowApplicationThunk(row),
        ).unwrap();

        const submittedRowId = submitRes?.application_id || row;

        if (submittedRowId) {
          dispatch(
            setAmenityROWField({
              name: "access_id",
              value: Number(submittedRowId) || submittedRowId,
            }),
          );
        }
        await fetchROW(submittedRowId);
        navigate(`/application-details-row`, {
          replace: true,
        });
      } else {
        license_type === "FINAL"
          ? await dispatch(
            submitFinalLegacyAccessApplicationThunk({
              application_id: access,
            }),
          )
          : await dispatch(
            submitLegacyAccessApplicationThunk({ application_id: access }),
          ).unwrap();
        await fetchAccess(access);

        navigate(`/application-details`, {
          replace: true,
        });
      }
      toast.success("Legacy Application Updated Successfully");
      return true;

      // final redirect
    } catch (error) {
      console.error("Error submitting consent form:", error);
      toast.error("Failed to submit form. Please try again.");
      return false;
    }
  };

  // BREADCRUMB
  const breadcrumbData = [
    {
      label: "Legacy Application",
      link: "#",
    },
  ];

  // STEP CONTENT RENDER
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <LegacyAmenityDetails
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            setSubmitHandler={setSubmitHandler}
            setIsStepValid={handleSetAmenityValid}
          />
        );

      case 1:
        return (
          <LegacySiteRoadwaysAccess
            collapsed={isSidePanelCollapsed}
            setSubmitHandler={setSubmitHandler}
            setIsStepValid={handleSetRoadwayValid}
          />
        );

      case 2:
        return (
          <LegacyUploadDocumentAccess
            collapsed={isSidePanelCollapsed}
            setSubmitHandler={setSubmitHandler}
            setIsStepValid={handleSetDocumentsValid}
          />
        );

      case 3:
        return (
          <LegacyUnderTakingConsent
            collapsed={isSidePanelCollapsed}
            access={access}
            isLegacyContent={true}
            setSubmitHandler={setSubmitHandler}
            setIsConsentValid={handleSetConsentValid}
          />
        );

      case 4:
        return (
          <LegacyPaymentDetail
            setIsStepValid={handleSetPaymentValid}
            setSubmitHandler={setSubmitHandler}
          />
        );

      default:
        return null;
    }
  };

  const renderStepContentROW = () => {
    switch (activeStep) {
      case 0:
        return (
          <LegacyAmenityDetailsRow
            onBack={goToPreviousStep}
            setSubmitHandler={setRowSubmitHandler}
            setIsStepValid={handleSetROWStepValid}
          />
        );

      case 1:
        return (
          <LegacySiteRoadwaysROW
            setSubmitHandler={setRowSubmitHandlerVoid}
            setIsStepValid={handleSetROWStepValid}
          />
        );

      case 2:
        return (
          <RowLegacyUploadDocuments
            collapsed={isSidePanelCollapsed}
            setSubmitHandler={setRowSubmitHandlerVoid}
            setIsStepValid={handleSetROWStepValid}
          />
        );

      case 3:
        return (
          <LegacyUnderTakingConsentRow
            collapsed={isSidePanelCollapsed}
            isDetailsPage={false}
            legacyContent={true}
            setSubmitHandler={setRowSubmitHandlerVoid}
            setIsConsentValid={handleSetROWConsentValid}
          />
        );

      case 4:
        return (
          <LegacyPaymentDetail
            setIsStepValid={handleSetROWPaymentValid}
            setSubmitHandler={(handler) => {
              setRowSubmitHandler(
                () => handler as () => Promise<boolean | void>,
              );
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="page-content">
      {/* Breadcrumb */}
      <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />

      {/* Header + Stepper */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={{
          mt: "1rem",
          flexDirection: { xs: "column", sm: "row" },
          width: "100%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            fontSize: { xs: "14px", md: "18px" },
            whiteSpace: "nowrap" // Fixed: moved outside of fontSize object
          }}
        >
          Legacy Application
        </Typography>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", sm: "block" } }}
        />

        {/* Step Pills UI Container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            overflowX: "auto",
            width: "100%",
            justifyContent: { xs: "flex-start", sm: "flex-end" }, // Aligns right on desktop, keeps scroll start on mobile
            py: 1, // Added padding to avoid clipping box-shadows/borders during scroll
            scrollbarWidth: "none", // Hides default scrollbar on Firefox
            "&::-webkit-scrollbar": { display: "none" }, // Hides default scrollbar on Chrome/Safari
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  pointerEvents: "none",
                  backgroundColor:
                    activeStep >= index
                      ? "rgba(58, 28, 115, 0.24)"
                      : "rgba(0, 0, 0, 0.05)",
                  color:
                    activeStep >= index
                      ? "rgba(58, 28, 115, 1)"
                      : "rgba(117, 117, 117, 1)",
                  border:
                    activeStep === index
                      ? "1px solid rgba(58, 28, 115, 1)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "25px",
                  whiteSpace: "nowrap", // Added: Prevents step labels from wrapping onto 2 lines on small screens
                  flexShrink: 0, // Added: Stops pills from squeezing/compressing horizontally
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    fontSize: 10,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      activeStep >= index
                        ? "rgba(58, 28, 115, 1)"
                        : "rgba(0,0,0,0.08)",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>

                <Typography sx={{ fontSize: 10, fontWeight: 600 }}>
                  {step.label}
                </Typography>
              </Box>

              {/* Connector Line (Optimized for scrolling row) */}
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    width: 20,
                    height: 1,
                    backgroundColor: "#d0d0d0",
                    mx: 0.5,
                    flexShrink: 0, // Added: Keeps the line visible and proportional during scroll
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>


      {/* Body */}
      <Box className="stepper-container">
        <Box className="step-body-wrapper">
          <Box
            className="step-body mx-auto position-relative"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            <Box className="left-panel">
              {moduleType === "row"
                ? renderStepContentROW()
                : renderStepContent()}
            </Box>
          </Box>
        </Box>
      </Box>

      <LegacyStepsButton
        onBack={goToPreviousStep}
        nextDisabled={
          moduleType === "row"
            ? activeStep === 3
              ? !rowConsentValid
              : activeStep === 4
                ? !rowPaymentValid
                : !rowStepValid
            : activeStep === 0
              ? !stepValidation.amenity
              : activeStep === 1
                ? !stepValidation.roadway
                : activeStep === 2
                  ? !stepValidation.documents
                  : activeStep === 3
                    ? !stepValidation.consent
                    : activeStep === 4
                      ? !stepValidation.payment
                      : false
        }
        onNext={async () => {
          const isLastStep = activeStep === steps.length - 1;

          try {
            // Child step submit
            const handlerToExecute =
              moduleType === "row"
                ? rowSubmitHandler
                : submitHandlerStep === activeStep
                  ? submitHandler
                  : null;

            if (!isLastStep) {
              if (handlerToExecute) {
                // store reference before rerender
                const submitFn = handlerToExecute;

                const submitResult = await submitFn();
                if (submitResult === false) {
                  return;
                }
              }

              // move only after success
              setLegacyActiveStep((prev: number) => prev + 1);

              return;
            }

            // Final submit
            if (isLastStep) {
              if (handlerToExecute) {
                const submitFn = handlerToExecute;
                const submitResult = await submitFn();
                if (submitResult === false) {
                  return;
                }

                // Redirect immediately after payment submit succeeds
                if (moduleType === "row") {
                  const rowDetails = await fetchROW(row);
                  navigate(
                    `/application-submitted?id=${rowDetails?.application_code}&legacy=true`,
                    {
                      replace: true,
                    },
                  );
                } else {
                  const accessDetails = await fetchAccess(access);
                  navigate(
                    `/application-submitted?id=${accessDetails?.application_code}&legacy=true`,
                    {
                      replace: true,
                    },
                  );
                }
              }
            }
          } catch (error) {
            console.error(error);
            toast.error("Failed to process this step. Please try again.");
            // step will NOT move
          }
        }}
      />
    </div>
  );
};

export default LegacyApplicationUpdate;
